<template>
    <div class="plan-container row">
        <div v-for="plan in grades" :key="plan.level" class="col-12 col-md-4">
            <div class="plan m-1 mb-4">
                <h3 class="plan-title my-2" v-html="$t('plan.' + plan.level.toLowerCase())"></h3>
                <div class="price">
                    {{currencyToDisplay}}$
                    <span class="price-large">{{priceLargePart(priceForGrade(plan)[currency]['monthly_price_cents'])}}</span>
                    <span class="price-small">{{priceSmallPart(priceForGrade(plan)[currency]['monthly_price_cents'])}}</span>
                    <span class="per-month">/{{$t('plan.month')}}</span>
                </div>
                <div class="d-flex align-items-center">
                    <div class="annually-pay-savings">
                        {{$t('plan.annually_pay_savings', {currency: currencyToDisplay, 'saving': annuallyPaySavings(plan)})}}
                    </div>
                </div>
                <div v-for="(value, key) in featuresForGrade(plan)" class="feature row">
                    <div class="col-12 col-lg-auto">
                        <div v-html="$t('plan.' + key)"></div>
                        <div v-if="getFeatureTips(plan, key)" v-b-tooltip.hover class="tip-icon" :title="getFeatureTips(plan, key)"></div>
                    </div>
                    <div v-if="value" class="feature-value col-12 col-lg-auto ml-lg-auto">
                        <span v-if="value === true" class="checked-icon"></span>
                        <div v-else v-html="value"></div>
                    </div>
                    <div v-else class="text-right col">
                        -
                    </div>
                    <div class="clearfix"></div>
                </div>
            </div>
			<div class="text-center">
				<a href="javascript:void(0)" @click="showSMSHintModal = true">{{$t('sms_rate')}}</a>
				<sms-rate-modal v-if="showSMSHintModal" @close="showSMSHintModal = false"></sms-rate-modal>
			</div>
        </div>
    </div>
</template>

<script>
import SMSRateModal from '../sms/SMSRateModal';
import buAPI from '../../bu/api';
import CapabilityManager from '../../bu/CapabilityManager';

export default {
    name: 'plans-display-only',
    components: { 'sms-rate-modal': SMSRateModal},
    props: {
        currency: {
            required: true,
            type: String
        },
        terms: {
            required: true,
            type: Object
        },
    },
    data() {
        let featureTips = {
            email_management: this.$t('plan.email_management_tips'),
            embed_button: this.$t('plan.embed_button_tips'),
            fb_integration: this.$t('plan.fb_integration_tips'),
            file_vault: this.$t('plan.file_vault_tip')
        };
        let gradeFeatureTips = {
            BASIC: {},
            STANDARD: {
            },
            PREMIUM: {
            }
        };

        return {
            grades: null,
            featureTips,
            gradeFeatureTips,
            showSMSHintModal: false
        };
    },
    computed: {
        currencyToDisplay() {
            return this.currency.toUpperCase();
        }
    },
    mounted() {
        this.getGrades();
    },
    methods: {
        async getGrades() {
            const response = await buAPI.getGrades();
            this.grades = response.data;
        },
        getFeatureTips(grade, feature) {
            if (this.gradeFeatureTips[grade.level][feature]) {
                return this.gradeFeatureTips[grade.level][feature];
            }

            return this.featureTips[feature];
        },
        priceForGrade(grade) {
            return  _.keyBy(grade.prices, 'currency');
        },
        titleForGrade(grade) {
            return this.$t('plan.' + grade.level.toLowerCase());
        },
        featuresForGrade(grade) {
            const capabilities = Object.values(grade.capabilities);
            for(var i in grade.grade_capabilities) {
                const gradeCapability = grade.grade_capabilities[i];
                const c = capabilities.find((c) => c.id === gradeCapability.capability_id)
                c.max = gradeCapability.max;
                c.enabled = gradeCapability.enabled;
            }
            const manager = new CapabilityManager(grade, capabilities);

            const compressionMap = {
                'BASIC': this.$t('plan.medium_photo_quality'),
                'STANDARD': this.$t('plan.medium_photo_quality'),
                'PREMIUM': this.$t('plan.high_photo_quality')
            }
            const base = {
                booking_management: true,
                reservation_form: true,
                landing_page: true,
                interstitial: true,
                shopping_bag_module: true,
                payment_integration: this.$t('plan.payment_integration_content'),
                team_member_cap: manager.getMaxTeamMemberCount(),
                team_calendar_management: manager.getMaxTeamMemberCount() > 1,
                portfolio_number: this.$t('plan.portfolio_number_content', {
                    album: manager.getMaxAlbumCount(),
                    photo_per_album: manager.getMaxPhotoCountPerAlbum(),
                }),
                compression: compressionMap[grade.level],
                customer_management: true,
                customer_login: true,
                customer_points: manager.canManageMemberPoints(),
                coupon_management: this.$t('plan.coupon_available_yearly', {maxCap: manager.getCouponAvailableCountPerYear()}),
                promocode_management: this.$t('plan.promocode_available_yearly', {maxCap: manager.getPromocodeAvailableCountPerYear()}),
                black_list: true,
                ['auto_notification' + (manager.isEmailModuleAvailable() ? '_with_manual' : '')]: this.emailCapHintForGrade(grade),
                sms_module: true,
                line_module: true,
                fb_integration: true,
                google_calendar_integration: manager.isGoogleCalendarModuleAvailable(),
                e_contract: manager.isContractModuleAvailable(),
                private_service: true,
                fb_customer_chat: manager.canShowFBCustomerChat(),
                embed_button: true,
                file_vault: this.$tc('plan.file_vault_content', 1, {
                    maxSize: `${manager.getFileVaultMaxSize() / 1000000} MB`,
                    singleMaxSize: `${manager.getFileVaultSingleFileMaxSize() / 1000000} MB`,
                    retentionMonth: manager.getFileVaultRetentionPeriodMonth()
                }),
            };
            if (manager.canCustomReminderHour()) {
                base.customize_customer_booking_reminder = true;
            } else {
                base.customer_booking_reminder = true;
            }
            return base;
        },
        emailCapHintForGrade(grade) {
            switch(grade.level) {
                case 'BASIC':
                    return this.$t('plan.email_cap_hint_basic');
                case 'STANDARD':
                    return this.$t('plan.email_cap_hint_standard');
                case 'PREMIUM':
                    return this.$t('plan.email_cap_hint_premium');
            }
            return '';
        },
        annuallyPaySavings(plan) {
            return (12 * this.priceForGrade(plan)[this.currency].monthly_price_cents / 100 * (1 - this.terms['year']['discount'])).toFixed(this.fixedForCurrency);
        },
        priceLargePart(price) {
            let string = String(price / 100);
            let indexOfDecimalPoint = string.indexOf(".");
            if (indexOfDecimalPoint >= 0) {
                return string.substr(0, indexOfDecimalPoint);
            }
            return string;
        },
        priceSmallPart(price) {
            let string = String(price / 100);
            let indexOfDecimalPoint = string.indexOf(".");
            if (indexOfDecimalPoint >= 0) {
                return string.substr(indexOfDecimalPoint + 1);
            }
            return "";
        }
    }

}
</script>

<style lang="scss" scoped>
    .plan-container {
        max-width: 1440px;
        margin-left: 8px;
    }

    .plan {
        background: white;
        border-radius: 8px;
        padding: 20px 16px 0;
        position: relative;
    }

    .price {
        text-align: center;
    }

    .plan-title {
        font-size: 1.75em;
        text-align: center;
        text-transform: lowercase;
        &::first-letter {
            text-transform: capitalize;
        }
    }

    .price-large {
        vertical-align: top;
        font-size: 2em;
    }

    .per-month {
        vertical-align: bottom;
        position: relative;
        bottom: 8px;
    }

    .annually-pay-savings {
        background: #FBF272;
        border-radius: 3px;
        margin: 1.2em auto 3em;
        padding: 0.2em 1em;
        text-align: center;
    }

    .feature {
        border-bottom: 1px solid #EEE;
        padding-bottom: 1em;
        margin-top: 1em;
    }

    .feature:last-child {
        border-bottom: none;
    }

    .feature-value {
        color: #B19122;
    }

    .checked-icon {
        width: 20px;
        height: 20px;
        display: inline-block;
        background-image: url(/img/feature_check.png);
    }

    .tip-icon {
        width: 17px;
        height: 17px;
        display: inline-block;
        background-image: url(/img/details@3x.png);
        background-size: contain;
        background-repeat: no-repeat;
        position: relative;
        top: 2px;
        left: 4px;
    }

    .action-btn-container {
        position: absolute;
        bottom: 16px;
        width: 90%;
    }

    .plan-duration-options-container {
        list-style: none;
        margin: 0;
        padding: 0 24px 0;
    }

    .plan-duration-option {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-items: center;
        min-height: 60px;
        padding: 4px 8px;
        cursor: pointer;
    }

    .plan-duration-option:hover {
        background-color: #EEE;
        border-radius: 8px;
    }

    .duration-option-radio {
        width: 20px;
        height: 20px;
        position: relative;
        display: inline-block;
        border-radius: 10px;
        border: 1px solid #0094FB;
    }

    .duration-option-radio.selected::after {
        content: ' ';
        display: inline-block;
        width: 12px;
        height: 12px;
        border-radius: 6px;
        background-color:#0094FB;
        position: relative;
        left: 3px;
        bottom: 3px;
        top: -4px;
    }

    .duration-title {
        padding: 2px 20px;
        min-width: 150px;
    }

    .summary-container {

        .col-12 {
            height: 2em;
            display: flex;
            align-items: center;
        }

        .field-title {
            text-align: right;
            justify-content: flex-end;
            display: flex;
        }


        @media (max-width: 768px) {
            .field-title {
                text-align: left;
                display: block;
            }
        }


        .calculation-container {
            background: rgb(244, 248, 249);
            padding-top: 10px;
            padding-bottom: 10px;
            margin-top: 10px;
            margin-bottom: 10px;
        }

        .coupon-applied-value {
            position: relative;
            left: -9px;
        }

        .subtotal {
            font-weight: bold;
            color: #0094FB;
        }

        .subtotal-separator {
            margin: 8px auto;
            width: calc(100% - 32px);
        }

        .clickable-option {
            padding: 2px 8px;
            border-radius: 4px;
            cursor: pointer;

            .option-title {
                padding: 4px 12px;
            }
        }

        .clickable-option:hover {
            background: #EEE;
        }

        input {
            background: #F4F8F9;
            border: none;
            width: auto;
            height: auto;
        }
    }
    
    .min-field-title-width {
        min-width: 120px;
        text-align: left;
    }

    .clearfix {
        clear: both;
    }

    .current-plan {
        border: 3px solid #F5C51E;
    }


</style>

<i18n>
{
	"en": {
		"sms_rate": "SMS Rate"
	},
	"zh": {
		"sms_rate": "簡訊儲值費率"
	}
}
</i18n>